import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Select, TextInput, Checkbox } from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/ReportService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'
injectReducer('finacialsReports', reducer)

const validationSchema = Yup.object().shape({
    company: Yup.string().required('please select company.'),
    // customer: Yup.string().required('please select customer.'),
    report_name: Yup.string().required('name field is required.'),
    report_url: Yup.string().required('url field is required.'),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { companyData, customersData } = useSelector(state => state.listData)
    const { view, record, loading } = useSelector(state => state.finacialsReports.data)

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = id => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/meson-insights/financials')

    const handleSubmit = data => {
        if (id) {
            updateApiRecord(id, data)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                })
                .catch(error => console.log('Error: ', error))
        } else {
            createApiRecord(data)
                .then(() => {
                    Success('Created Successfully!')
                    handleDiscard()
                })
                .catch(error => console.log('Error: ', error))
        }
    }

    if (loading && id) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Reports</h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">{id ? 'Update Report' : 'Add New Report'}</h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={values => {
                                handleSubmit(values)
                            }}
                        >
                            {({ values, touched, errors, setFieldValue }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                            <Field name="company">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="company"
                                                        name="company"
                                                        label="Company"
                                                        placeholder="Company"
                                                        value={values?.company}
                                                        options={companyData}
                                                        onChange={e => {
                                                            form.setFieldValue(field.name, e.target.value)
                                                            form.setFieldValue('customer', '')
                                                        }}
                                                        error={touched.company && errors.company}
                                                        message={errors.company}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="customer">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="customer"
                                                        name="customer"
                                                        label="Customer"
                                                        placeholder="Customer"
                                                        value={values?.customer}
                                                        options={customersData?.filter(
                                                            item => item?.company?.id?.toString() === values?.company?.toString()
                                                        )}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.customer && errors.customer}
                                                        message={errors.customer}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="report_name">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        label="Report Name"
                                                        name="report_name"
                                                        error={errors.report_name && touched.report_name}
                                                        message={errors.report_name}
                                                        value={values?.report_name}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="report_url">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        label="Report Url"
                                                        name="report_url"
                                                        error={errors.report_url && touched.report_url}
                                                        message={errors.report_url}
                                                        value={values?.report_url}
                                                        onChange={e => {
                                                            const url = e.target.value
                                                            form.setFieldValue(field.name, url)
                                                            // Auto-detect url type and default masking if user hasn't toggled manually
                                                            try {
                                                                const u = new URL(url)
                                                                const host = u.hostname || ''
                                                                const path = u.pathname || ''
                                                                let detected = 'external'
                                                                let defaultMask = false
                                                                if (host.includes('metabase.maestro.ensemblesolutions.com.au')) {
                                                                    if (path.startsWith('/public/')) {
                                                                        detected = 'metabase_public'
                                                                        defaultMask = true
                                                                    } else {
                                                                        detected = 'metabase_private'
                                                                        defaultMask = false
                                                                    }
                                                                }
                                                                // Enforce masking only for public metabase; otherwise force false
                                                                if (detected !== 'metabase_public') {
                                                                    form.setFieldValue('use_masking', false)
                                                                } else if (!touched?.use_masking) {
                                                                    // For public, default to true unless user has already toggled
                                                                    form.setFieldValue('use_masking', defaultMask)
                                                                }
                                                                // We display detected type via helper text below
                                                                form.setFieldValue('url_type', detected, false)
                                                            } catch (err) {
                                                                // Invalid URL -> treat as external and disable masking
                                                                form.setFieldValue('use_masking', false)
                                                                form.setFieldValue('url_type', 'external', false)
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                            <Field name="use_masking">
                                                {({ field, form }) => (
                                                    <div className="flex flex-col">
                                                        <Checkbox
                                                            id="use_masking"
                                                            name="use_masking"
                                                            label="Mask via NGINX (auto-detected)"
                                                            checked={!!values?.use_masking}
                                                            onChange={e => form.setFieldValue(field.name, e.target.checked)}
                                                            disabled={values?.url_type !== 'metabase_public'}
                                                        />
                                                        <span className="mt-1 text-xs text-gray-500">
                                                            Detected URL type: {values?.url_type || 'unknown'}
                                                            {values?.url_type !== 'metabase_public' &&
                                                                ' — masking available only for Metabase public URLs'}
                                                        </span>
                                                    </div>
                                                )}
                                            </Field>
                                            <Field name="description">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        label="Description"
                                                        name="description"
                                                        error={errors.description && touched.description}
                                                        message={errors.description}
                                                        value={values?.description}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-3">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
